/**
 * The Inspector header (reference 02): the entity's icon and name, a
 * visibility toggle, and tags for its class and level asset path.
 *
 * Renaming commits on Enter or blur and Escape reverts, like Unreal's details
 * panel (bindings/useRename.ts, shared with the Outliner).
 *
 * With several entities selected, the header counts them and its visibility
 * toggle hides or shows them all, following the active entity's state (as H).
 */
import type { EntityRecord } from "../../world/entity";
import { useRename } from "../bindings/useRename";
import { runEdit, useEditor } from "../EditorContext";
import { setVisibleEdit } from "../selection/selectionEdits";
import { useWorldSnapshot } from "../hooks/editorHooks";
import { entityDisplay } from "../outliner/outlinerModel";
import { EntityTypeIcon, EyeIcon, EyeOffIcon } from "../ui/icons";

export function InspectorHeader({ entity }: { entity: EntityRecord }) {
  const { components } = useEditor();
  const levelPath = useLevelAssetPath();

  return (
    <header className="kn-inspector__header">
      <div className="kn-inspector__identity">
        <EntityTypeIcon
          name={entityDisplay(entity, components).icon}
          size={14}
          className="kn-inspector__icon"
        />
        {/* Keyed by entity and name: a new selection or an undone rename resets the draft. */}
        <NameField key={`${entity.id}:${entity.name}`} entity={entity} />
        <VisibilityToggle entities={[entity]} active={entity} />
      </div>
      <div className="kn-inspector__tags">
        <span className="kn-tag">{entity.className ?? "Entity"}</span>
        {levelPath && <span className="kn-tag">{levelPath}</span>}
      </div>
    </header>
  );
}

/** Several entities selected: a count, and one visibility toggle for all of them. */
export function MultiHeader({
  entities,
  active,
}: {
  entities: readonly EntityRecord[];
  active: EntityRecord;
}) {
  return (
    <header className="kn-inspector__header">
      <div className="kn-inspector__identity">
        <span className="kn-inspector__count">{entities.length} entities selected</span>
        <VisibilityToggle entities={entities} active={active} />
      </div>
    </header>
  );
}

/** The eye: hides the entities, or shows them when the active one is hidden. */
function VisibilityToggle({
  entities,
  active,
}: {
  entities: readonly EntityRecord[];
  active: EntityRecord;
}) {
  const { dispatch, dispatchAll } = useEditor();
  const { visible } = active;

  return (
    <button
      type="button"
      className="kn-icon-button"
      aria-label={visible ? "Hide" : "Show"}
      title={visible ? "Hide in viewport" : "Show in viewport"}
      aria-pressed={!visible}
      onClick={() => {
        runEdit({ dispatch, dispatchAll }, setVisibleEdit(entities, !visible, "inspector"));
      }}
    >
      {visible ? <EyeIcon size={14} /> : <EyeOffIcon size={14} />}
    </button>
  );
}

function NameField({ entity }: { entity: EntityRecord }) {
  const { draft, setDraft, error, commit, revert } = useRename(entity, "inspector");

  return (
    <span className="kn-field kn-inspector__name">
      <input
        type="text"
        className="kn-input kn-input--mono"
        aria-label="Name"
        value={draft}
        {...(error ? { "aria-invalid": true, "aria-describedby": "inspector-name-error" } : {})}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          commit();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") revert();
        }}
      />
      {error && (
        <span id="inspector-name-error" className="kn-field__error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

function useLevelAssetPath(): string | null {
  const { content } = useEditor();
  const { levelId } = useWorldSnapshot();
  return content.index.levels.find((level) => level.id === levelId)?.assetPath ?? null;
}
